import {
  IoCheckmarkCircleOutline,
  IoDocumentTextOutline,
  IoFilterOutline,
  IoOpenOutline,
  IoSearchOutline,
  IoTimeOutline,
} from "react-icons/io5";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";

import DataTable from "react-data-table-component";
import { ErrorBanner } from "../../components/banner";
import { Link } from "react-router-dom";
import { Loading } from "../../components/loader";
import Moment from "moment";
import { Section } from "../../components/section";
import { fetchApplications } from "../../redux/careers";
import { shadow } from "../../assets/styles/parameters";

export const ApplicationsList = () => {
  const dispatch = useDispatch();
  const { applications, loading, error } = useSelector(
    (state) => state.careers,
  );

  // --- FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [jobFilter, setJobFilter] = useState("ALL");

  useEffect(() => {
    dispatch(fetchApplications());
  }, [dispatch]);

  // --- HR PIPELINE STATS & DYNAMIC FILTERS ---
  const { total, pending, shortlisted, uniqueJobs } = useMemo(() => {
    // Extract unique job titles for our dropdown filter
    const jobs = [
      ...new Set(applications.map((a) => a.job?.title).filter(Boolean)),
    ];

    return {
      total: applications.length,
      pending: applications.filter((a) => a.status === "PENDING").length,
      shortlisted: applications.filter((a) => a.status === "SHORTLISTED")
        .length,
      uniqueJobs: jobs,
    };
  }, [applications]);

  // --- THE FILTERING LOGIC ---
  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      // 1. Search Logic (Checks Name and Email)
      const fullName =
        `${app.candidate?.firstName} ${app.candidate?.lastName}`.toLowerCase();
      const email = app.candidate?.email?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        fullName.includes(searchLower) || email.includes(searchLower);

      // 2. Status Logic
      const matchesStatus =
        statusFilter === "ALL" || app.status === statusFilter;

      // 3. Job Logic
      const matchesJob = jobFilter === "ALL" || app.job?.title === jobFilter;

      return matchesSearch && matchesStatus && matchesJob;
    });
  }, [applications, searchTerm, statusFilter, jobFilter]);

  // --- HELPER: COLOR CODED STATUS BADGES ---
  const getStatusBadge = (status) => {
    const styles = {
      PENDING: { bg: "#FFF4E6", color: "#F39C12" },
      REVIEWED: { bg: "#E6F2FF", color: "#3B5BDB" },
      SHORTLISTED: { bg: "#F4ECF7", color: "#9B59B6" },
      HIRED: { bg: "#E8F8F5", color: "#2ECA7F" },
      REJECTED: { bg: "#FDEDEC", color: "#E74C3C" },
    };
    const style = styles[status] || { bg: "#F3F4F6", color: "#4B5563" };
    return (
      <StatusBadge bg={style.bg} color={style.color}>
        {status}
      </StatusBadge>
    );
  };

  // --- TABLE COLUMNS ---
  const columns = [
    {
      name: "Applied Date",
      selector: (row) => Moment(row.createdDate).format("MMM DD, YYYY"),
      sortable: true,
      width: "140px",
    },
    {
      name: "Candidate",
      selector: (row) =>
        `${row.candidate?.firstName} ${row.candidate?.lastName}`,
      sortable: true,
      style: { fontWeight: 600, color: "#111" },
    },
    {
      name: "Job Role",
      selector: (row) => row.job?.title || "Unknown Role",
      sortable: true,
    },
    {
      name: "Pipeline Status",
      selector: (row) => row.status,
      cell: (row) => getStatusBadge(row.status),
      sortable: true,
      width: "150px",
    },
    {
      name: "Action",
      cell: (row) => (
        <ActionLink to={`edit/${row.id}`} state={row}>
          Review <IoOpenOutline />
        </ActionLink>
      ),
      button: true,
      width: "120px",
    },
  ];

  if (loading && applications.length === 0) return <Loading />;

  return (
    <Section>
      {error && <ErrorBanner text={error} />}

      <StatsGrid>
        <StatCard delay="0.1s">
          <StatHeader>
            <IconWrapper bg="#F4ECF7" color="#9B59B6">
              <IoDocumentTextOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{total}</StatValue>
          <StatLabel>Total Applications</StatLabel>
        </StatCard>

        <StatCard delay="0.2s">
          <StatHeader>
            <IconWrapper bg="#FFF4E6" color="#F39C12">
              <IoTimeOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{pending}</StatValue>
          <StatLabel>Needs Review (Pending)</StatLabel>
        </StatCard>

        <StatCard delay="0.3s">
          <StatHeader>
            <IconWrapper bg="#E8F8F5" color="#2ECA7F">
              <IoCheckmarkCircleOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{shortlisted}</StatValue>
          <StatLabel>Shortlisted Candidates</StatLabel>
        </StatCard>
      </StatsGrid>

      <WidgetCard delay="0.4s">
        {/* --- NEW FILTER BAR --- */}
        <FilterBar>
          <SearchWrapper>
            <IoSearchOutline size={20} color="#9CA3AF" />
            <SearchInput
              type="text"
              placeholder="Search candidate name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>

          <FilterGroup>
            <FilterWrapper>
              <IoFilterOutline color="#6B7280" />
              <FilterSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="REVIEWED">Reviewed</option>
                <option value="SHORTLISTED">Shortlisted</option>
                <option value="HIRED">Hired</option>
                <option value="REJECTED">Rejected</option>
              </FilterSelect>
            </FilterWrapper>

            <FilterWrapper>
              <IoFilterOutline color="#6B7280" />
              <FilterSelect
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
              >
                <option value="ALL">All Roles</option>
                {uniqueJobs.map((job) => (
                  <option key={job} value={job}>
                    {job}
                  </option>
                ))}
              </FilterSelect>
            </FilterWrapper>
          </FilterGroup>
        </FilterBar>

        <DataTable
          columns={columns}
          // Pass the dynamically filtered array instead of the raw Redux array
          data={filteredApplications}
          striped
          highlightOnHover
          pagination
          responsive
          customStyles={tableStyles}
          defaultSortFieldId={1}
          defaultSortAsc={false}
        />
      </WidgetCard>
    </Section>
  );
};

// --- ANIMATIONS & STYLED COMPONENTS ---

const slideUpFade = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  border: 1px solid rgba(0, 0, 0, 0.02);
`;

const StatHeader = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const StatValue = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px 0;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const WidgetCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  overflow: hidden;
`;

/* --- NEW FILTER STYLES --- */
const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f3f4f6;
  flex-wrap: wrap;

  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  padding: 10px 16px;
  border-radius: 8px;
  flex: 1;
  /* Remove fixed min-width — it forces the page wider than the viewport on phones */
  min-width: 0;
  transition: all 0.2s;
  &:focus-within {
    border-color: #3b5bdb;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 91, 219, 0.1);
  }
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  font-size: 14px;
  color: #111;
  &::placeholder {
    color: #9ca3af;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
`;

const FilterSelect = styled.select`
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #4b5563;
  font-weight: 500;
  cursor: pointer;
  &:focus {
    color: #111;
  }
`;

const StatusBadge = styled.span`
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: inline-block;
`;

const ActionLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3b5bdb;
  font-weight: 600;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  transition: background 0.2s;
  &:hover {
    background: #e6f2ff;
  }
`;

const tableStyles = {
  headRow: {
    style: { borderBottomColor: "#f0f0f0", fontWeight: 600, color: "#555" },
  },
  rows: {
    style: {
      minHeight: "56px",
      "&:not(:last-of-type)": { borderBottomColor: "#f0f0f0" },
    },
  },
};
